import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, MousePointerClick, Eye, TrendingUp } from "lucide-react";
import AdSlot from "@/components/AdSlot";

const PLACEMENTS = [
  { value: "all", label: "Tous les emplacements" },
  { value: "home", label: "Page d'accueil" },
  { value: "blog", label: "Blog (article)" },
  { value: "model_detail", label: "Fiche modèle" },
];

const ICON_OPTIONS = ["Droplets", "Bot", "Shield", "ShieldCheck", "GraduationCap", "Wrench", "Sparkles", "Star", "Zap"];

const EMPTY_FORM = {
  advertiser_name: "",
  title: "",
  subtitle: "",
  cta_label: "En savoir plus",
  target_url: "",
  image_url: "",
  gradient_from: "#2563eb",
  gradient_to: "#0f172a",
  icon: "Sparkles",
  placement: "all",
  active: true,
  weight: 10,
};

export default function AdminAds() {
  const { user, loading: authLoading } = useAuth();
  const [ads, setAds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/ads/"),
      api.get("/admin/ads/analytics", { params: { days: 30 } }),
    ])
      .then(([a, an]) => {
        setAds(a.data.items || []);
        setAnalytics(an.data);
      })
      .catch(() => toast.error("Impossible de charger les pubs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authLoading || !user || user.role !== "admin") return;
    reload();
  }, [user, authLoading]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (ad) => {
    setEditing(ad);
    setForm({
      advertiser_name: ad.advertiser_name || "",
      title: ad.title || "",
      subtitle: ad.subtitle || "",
      cta_label: ad.cta_label || "En savoir plus",
      target_url: ad.target_url || "",
      image_url: ad.image_url || "",
      gradient_from: ad.gradient_from || "#2563eb",
      gradient_to: ad.gradient_to || "#0f172a",
      icon: ad.icon || "Sparkles",
      placement: ad.placement || "all",
      active: ad.active,
      weight: ad.weight || 10,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.advertiser_name || !form.title || !form.target_url) {
      toast.error("Nom annonceur, titre et URL cible sont requis");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/ads/${editing.id}`, form);
        toast.success("Pub mise à jour");
      } else {
        await api.post("/admin/ads/", form);
        toast.success("Pub créée");
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Supprimer la pub "${name}" ?`)) return;
    try {
      await api.delete(`/admin/ads/${id}`);
      toast.success("Pub supprimée");
      reload();
    } catch (_e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (authLoading || !user) return null;
  if (user.role !== "admin") {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">Accès refusé</h1>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="admin-ads-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Admin · Publicités</div>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black tracking-tighter">
            Gestion des <span className="text-primary">bandeaux publicitaires</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Rotation aléatoire pondérée par weight — 6 pubs factices seedées par défaut.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="btn-new-ad" className="rounded-full h-11 px-6">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle pub
        </Button>
      </div>

      {/* KPIs */}
      {analytics && (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="ads-kpis">
          <Card className="p-5" data-testid="kpi-impressions">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Impressions (30j)</div>
                <div className="mt-2 font-display text-3xl font-black text-secondary">{analytics.impressions}</div>
              </div>
              <Eye className="h-6 w-6 shrink-0 text-secondary opacity-60" />
            </div>
          </Card>
          <Card className="p-5" data-testid="kpi-ad-clicks">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Clics (30j)</div>
                <div className="mt-2 font-display text-3xl font-black text-primary">{analytics.clicks}</div>
              </div>
              <MousePointerClick className="h-6 w-6 shrink-0 text-primary opacity-60" />
            </div>
          </Card>
          <Card className="p-5" data-testid="kpi-ctr">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">CTR moyen</div>
                <div className="mt-2 font-display text-3xl font-black text-emerald-700">{analytics.ctr_pct}%</div>
              </div>
              <TrendingUp className="h-6 w-6 shrink-0 text-emerald-700 opacity-60" />
            </div>
          </Card>
        </div>
      )}

      {/* Preview live */}
      <div className="mt-10">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Aperçu live (rotation toutes les 45s)</div>
        <AdSlot placement="all" />
      </div>

      {/* Ads table */}
      <div className="mt-10">
        <h2 className="font-display text-xl font-bold tracking-tight mb-4">Toutes les pubs ({ads.length})</h2>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            {ads.map((ad) => (
              <Card key={ad.id} className={`p-4 ${!ad.active ? "opacity-50" : ""}`} data-testid={`ad-row-${ad.id}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div
                    className="h-12 w-12 rounded-lg shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${ad.gradient_from || "#2563eb"} 0%, ${ad.gradient_to || "#0f172a"} 100%)`,
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{ad.title}</span>
                      {ad.is_fake && <Badge variant="outline" className="text-[9px] rounded-full">DÉMO</Badge>}
                      <Badge variant="secondary" className="text-[10px] rounded-full">{ad.placement}</Badge>
                      <Badge variant="outline" className="text-[10px] rounded-full">poids {ad.weight}</Badge>
                      {!ad.active && <Badge variant="outline" className="text-[10px] rounded-full text-red-700 border-red-200">inactif</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{ad.advertiser_name}</div>
                    <div className="text-xs mt-1 flex items-center gap-3 text-muted-foreground">
                      <span><Eye className="h-3 w-3 inline mr-1" />{ad.impressions_count} impr.</span>
                      <span><MousePointerClick className="h-3 w-3 inline mr-1" />{ad.clicks_count} clics</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(ad)} data-testid={`btn-edit-${ad.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(ad.id, ad.title)} data-testid={`btn-delete-${ad.id}`}>
                      <Trash2 className="h-3 w-3 text-red-600" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ad-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier la pub" : "Nouvelle pub"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nom de l'annonceur *</Label>
                <Input value={form.advertiser_name} onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })} data-testid="form-advertiser" />
              </div>
              <div>
                <Label>Emplacement</Label>
                <select className="w-full mt-1 rounded-md border border-input h-10 px-3 text-sm" value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} data-testid="form-placement">
                  {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Textarea value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} rows={2} data-testid="form-subtitle" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Label du CTA</Label>
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="form-cta" />
              </div>
              <div>
                <Label>URL cible *</Label>
                <Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://..." data-testid="form-url" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>Gradient début</Label>
                <Input type="color" value={form.gradient_from || "#2563eb"} onChange={(e) => setForm({ ...form, gradient_from: e.target.value })} className="h-10" data-testid="form-grad-from" />
              </div>
              <div>
                <Label>Gradient fin</Label>
                <Input type="color" value={form.gradient_to || "#0f172a"} onChange={(e) => setForm({ ...form, gradient_to: e.target.value })} className="h-10" data-testid="form-grad-to" />
              </div>
              <div>
                <Label>Icône</Label>
                <select className="w-full mt-1 rounded-md border border-input h-10 px-3 text-sm" value={form.icon || "Sparkles"} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="form-icon">
                  {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label>Poids (1-1000)</Label>
                <Input type="number" min="1" max="1000" value={form.weight} onChange={(e) => setForm({ ...form, weight: parseInt(e.target.value) || 10 })} data-testid="form-weight" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="form-active" />
              <Label>Pub active</Label>
            </div>
            {/* Preview */}
            <div>
              <Label>Aperçu du bandeau</Label>
              <div className="mt-2 rounded-2xl overflow-hidden shadow-md" style={{ background: `linear-gradient(135deg, ${form.gradient_from} 0%, ${form.gradient_to} 100%)` }}>
                <div className="px-6 py-5 text-white">
                  <div className="text-xs opacity-60 uppercase tracking-widest">Publicité · {form.advertiser_name || "Annonceur"}</div>
                  <div className="mt-1 font-display font-bold text-lg">{form.title || "Titre de la pub"}</div>
                  {form.subtitle && <div className="mt-1 text-sm opacity-85">{form.subtitle}</div>}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-ad">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
